import { sendCameraEvent } from '../services/packingProofEventsService'

const MAX_RETRIES = 3
// BACKOFF_MS.length must equal MAX_RETRIES - 1 (one delay per retry between attempts).
const BACKOFF_MS = [500, 1500]
const MAX_CONTEXT_BYTES = 4096
const MAX_TEXT_FIELD_LENGTH = 500

class CameraEventQueue {
  constructor() {
    this.queue = []
    this.processing = false
  }

  __reset__() {
    this.queue = []
    this.processing = false
  }

  enqueue(event) {
    const sanitized = this._sanitize(event)
    if (!sanitized) return
    this.queue.push({ event: sanitized, attempts: 0 })
    this._processNext()
  }

  _sanitize(event) {
    if (!event) return null
    const cloned = { ...event, context: event.context ? { ...event.context } : null }
    if (cloned.context?.error_message && cloned.context.error_message.length > MAX_TEXT_FIELD_LENGTH) {
      cloned.context.error_message = cloned.context.error_message.slice(0, MAX_TEXT_FIELD_LENGTH)
    }
    if (cloned.context?.user_agent && cloned.context.user_agent.length > MAX_TEXT_FIELD_LENGTH) {
      cloned.context.user_agent = cloned.context.user_agent.slice(0, MAX_TEXT_FIELD_LENGTH)
    }
    if (cloned.context) {
      const size = new Blob([JSON.stringify(cloned.context)]).size
      if (size > MAX_CONTEXT_BYTES) {
        console.warn('[cameraEventQueue] dropping event: context exceeds 4KB after truncation', {
          status: cloned.status,
          size,
        })
        return null
      }
    }
    return cloned
  }

  async _processNext() {
    if (this.processing) return
    if (this.queue.length === 0) return
    this.processing = true
    const item = this.queue[0]

    try {
      await sendCameraEvent(item.event)
      this.queue.shift()
    } catch (error) {
      const status = error?.response?.status
      // A 401 reaching the queue means axios-auth-refresh has already tried
      // to refresh and failed (a successful refresh would have retried the
      // request transparently and resolved). Retrying here is pointless and
      // would race the redirect-to-login on the interceptor side, so flush
      // the queue.
      if (status === 401) {
        console.warn('[cameraEventQueue] flushing queue: 401 after auth refresh failed')
        this.queue = []
        this.processing = false
        return
      }
      const isClientError = status >= 400 && status < 500
      if (isClientError) {
        console.warn('[cameraEventQueue] dropping event: client error', {
          status,
          code: error?.response?.data?.code,
          event: item.event,
        })
        this.queue.shift()
      } else if (item.attempts >= MAX_RETRIES - 1) {
        console.warn('[cameraEventQueue] dropping event: retries exhausted', {
          attempts: item.attempts + 1,
          event: item.event,
        })
        this.queue.shift()
      } else {
        item.attempts += 1
        const delay = BACKOFF_MS[item.attempts - 1] ?? BACKOFF_MS[BACKOFF_MS.length - 1]
        await new Promise(resolve => setTimeout(resolve, delay))
        this.processing = false
        return this._processNext()
      }
    }

    this.processing = false
    if (this.queue.length > 0) {
      this._processNext()
    }
  }
}

const cameraEventQueue = new CameraEventQueue()
export default cameraEventQueue
